import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ThreadMessageDto } from '@aiai/shared';
import { ArrowDown, Bot, Send } from 'lucide-react';
import type { ReactNode } from 'react';
import { fetchThreadMessages, postThreadMessage } from '../../api';
import { qk } from '../../api/queryKeys';
import { useAuth } from '../../auth/useAuth';
import { errorMessage } from '../../lib/api';
import { annotateMessages, sameDay } from '../../lib/threadMessages';
import { Markdown } from '../Markdown';
import { Spinner } from '../Spinner';
import { Callout } from '../Callout';
import { PmThinkingDots } from '../PmThinkingDots';

/** One page of history — passed explicitly to the API (which clamps 1–100) so
 *  the "a full page came back ⇒ there may be more above" check can never drift
 *  from the server's default. */
const PAGE_SIZE = 50;
const MAX_LEN = 10000;
const COUNTER_THRESHOLD = 9500;

// A small neutral palette for human avatars — deterministic per email so a
// given person always reads the same, but muted so no single sender shouts.
const AVATAR_TINTS: ReadonlyArray<{ bg: string; fg: string }> = [
  { bg: '#E7E5E4', fg: '#57534E' },
  { bg: '#E4E4E7', fg: '#52525B' },
  { bg: '#E5E7EB', fg: '#4B5563' },
  { bg: '#E2E8F0', fg: '#475569' },
  { bg: '#EAE6E1', fg: '#6B5E52' },
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function tintFor(seed: string): { bg: string; fg: string } {
  return AVATAR_TINTS[hashString(seed) % AVATAR_TINTS.length];
}

function initialsFor(email: string | undefined): string {
  const local = (email ?? '?').split('@')[0];
  const parts = local.split(/[.\-_+]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase() || '?';
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(iso, today.toISOString())) return 'Today';
  if (sameDay(iso, yesterday.toISOString())) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

function dedupeById(messages: ThreadMessageDto[]): ThreadMessageDto[] {
  const seen = new Set<string>();
  const out: ThreadMessageDto[] = [];
  for (const m of messages) {
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    out.push(m);
  }
  return out;
}

function Avatar({ message }: { message: ThreadMessageDto }) {
  if (message.role === 'pm') {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink text-surface-1">
        <Bot className="h-4 w-4" aria-hidden="true" />
      </div>
    );
  }
  const tint = tintFor(message.authorEmail ?? message.authorId ?? '?');
  return (
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-2xs font-semibold"
      style={{ backgroundColor: tint.bg, color: tint.fg }}
      aria-hidden="true"
    >
      {initialsFor(message.authorEmail)}
    </div>
  );
}

/**
 * The discussion column of a thread: posts oldest-first with Slack-style
 * grouping and day dividers, "Load earlier" paging at the top, the PM-thinking
 * row and failed-turn hint, and the composer pinned at the bottom (Enter
 * posts, Shift+Enter breaks a line).
 */
export function ThreadMessages({
  projectId,
  threadId,
  readOnly = false,
  readOnlyMessage = <>You don&apos;t have permission to post in this thread.</>,
  isPmThinking = false,
  lastTurnFailed = false,
}: {
  projectId: string;
  threadId: string;
  readOnly?: boolean;
  /** Shown in place of the composer while `readOnly`. */
  readOnlyMessage?: ReactNode;
  isPmThinking?: boolean;
  lastTurnFailed?: boolean;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const atBottomRef = useRef(true);
  const prevCountRef = useRef(0);

  const [showJumpPill, setShowJumpPill] = useState(false);

  // Newest page (server returns newest-first). Older pages accumulate below.
  const messagesQuery = useQuery({
    queryKey: qk.threadMessages(projectId, threadId),
    queryFn: () => fetchThreadMessages(projectId, threadId, undefined, PAGE_SIZE),
    enabled: !!projectId && !!threadId,
  });

  const [earlier, setEarlier] = useState<ThreadMessageDto[]>([]);
  const [lastEarlierCount, setLastEarlierCount] = useState<number | null>(null);
  const pendingScrollAdjust = useRef<number | null>(null);
  // True from the moment an "earlier" page lands until the passive
  // messages-effect has observed it. The scroll-restore layout effect runs
  // FIRST in the same commit and nulls `pendingScrollAdjust`, so that ref
  // cannot double as the guard — this flag is cleared only by the passive
  // effect itself, guaranteeing a prepend never reads as "new messages
  // arrived" (which would flash the jump-to-latest pill).
  const justPrependedRef = useRef(false);

  // Reset the accumulated history AND per-thread scroll state when switching
  // threads, so the next one gets its own initial jump-to-latest.
  useEffect(() => {
    setEarlier([]);
    setLastEarlierCount(null);
    setShowJumpPill(false);
    prevCountRef.current = 0;
    atBottomRef.current = true;
    pendingScrollAdjust.current = null;
    justPrependedRef.current = false;
  }, [projectId, threadId]);

  const baseAsc = useMemo(() => [...(messagesQuery.data ?? [])].reverse(), [messagesQuery.data]);
  const messages = useMemo(() => dedupeById([...earlier, ...baseAsc]), [earlier, baseAsc]);
  const annotated = useMemo(() => annotateMessages(messages), [messages]);

  const hasMore = lastEarlierCount === null ? baseAsc.length >= PAGE_SIZE : lastEarlierCount >= PAGE_SIZE;

  const loadEarlierMutation = useMutation({
    mutationFn: () => {
      const oldest = messages[0]?.createdAt;
      return fetchThreadMessages(projectId, threadId, oldest, PAGE_SIZE);
    },
    onSuccess: (older) => {
      setLastEarlierCount(older.length);
      // Only arm the scroll-restore + prepend guards when something will
      // actually prepend — an empty page re-renders nothing, so the effects
      // that consume (and clear) these flags would never run, and the stale
      // flags would corrupt handling of the next real message.
      if (older.length > 0) {
        const el = scrollRef.current;
        pendingScrollAdjust.current = el ? el.scrollHeight - el.scrollTop : null;
        justPrependedRef.current = true;
        setEarlier((prev) => dedupeById([...[...older].reverse(), ...prev]));
      }
    },
  });

  // Restore scroll position after an "earlier" prepend so the viewport stays put.
  useLayoutEffect(() => {
    if (pendingScrollAdjust.current != null && scrollRef.current) {
      const el = scrollRef.current;
      el.scrollTop = el.scrollHeight - pendingScrollAdjust.current;
      pendingScrollAdjust.current = null;
    }
  }, [messages]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    bottomRef.current?.scrollIntoView({ behavior });
    setShowJumpPill(false);
    atBottomRef.current = true;
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const near = distance < 120;
    atBottomRef.current = near;
    if (near) setShowJumpPill(false);
  }, []);

  // Auto-scroll on new posts only when the reader is already near the bottom;
  // otherwise surface the "jump to latest" pill so we never yank their place.
  useEffect(() => {
    const count = messages.length;
    const grew = count > prevCountRef.current;
    if (justPrependedRef.current) {
      justPrependedRef.current = false;
    } else if (prevCountRef.current === 0 && count > 0) {
      requestAnimationFrame(() => scrollToBottom('auto'));
    } else if (grew) {
      if (atBottomRef.current) scrollToBottom('smooth');
      else setShowJumpPill(true);
    }
    prevCountRef.current = count;
  }, [messages, scrollToBottom]);

  // Keep the thinking indicator in view for a reader sitting at the bottom.
  useEffect(() => {
    if (isPmThinking && atBottomRef.current) scrollToBottom('smooth');
  }, [isPmThinking, scrollToBottom]);

  // Auto-grow the composer between 1 and ~6 rows.
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 150)}px`;
  }, [draft]);

  const sendMutation = useMutation({
    mutationFn: (body: string) => postThreadMessage(projectId, threadId, body),
    onSuccess: () => {
      setDraft('');
      atBottomRef.current = true;
      // Root thread key: refreshes this thread's posts, its detail (post
      // count, reopened state) and the list's ordering in one invalidation.
      void queryClient.invalidateQueries({ queryKey: qk.threads(projectId) });
    },
  });

  const handleSend = () => {
    const body = draft.trim();
    if (body && !sendMutation.isPending) sendMutation.mutate(body);
  };

  const senderName = (m: ThreadMessageDto): string => {
    if (m.role === 'pm') return 'PM';
    if (m.authorId && m.authorId === user?.id) return 'You';
    return m.authorEmail ?? 'Unknown';
  };

  const isLoading = messagesQuery.isLoading;
  const isEmpty = !isLoading && messages.length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex h-full flex-col gap-1 overflow-y-auto rounded-lg border border-line bg-surface p-4"
        >
          {hasMore && (
            <div className="flex justify-center pb-2">
              <button
                type="button"
                className="btn-ghost text-xs"
                disabled={loadEarlierMutation.isPending}
                onClick={() => loadEarlierMutation.mutate()}
              >
                {loadEarlierMutation.isPending ? <Spinner className="h-3.5 w-3.5" /> : null}
                Load earlier posts
              </button>
            </div>
          )}

          {isLoading && (
            <div className="flex flex-1 items-center justify-center py-8">
              <Spinner className="h-4 w-4" />
            </div>
          )}

          {isEmpty && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 py-10 text-center">
              <p className="text-sm text-ink-muted">No posts yet.</p>
            </div>
          )}

          {annotated.map(({ message, showDayDivider, showHeader }) => (
            <div key={message.id}>
              {showDayDivider && (
                <div className="my-3 flex items-center gap-3">
                  <span className="h-px flex-1 bg-line" />
                  <span className="rounded-full border border-line bg-surface-2 px-2.5 py-0.5 font-display text-2xs font-bold uppercase tracking-wide text-ink-faint">
                    {dayLabel(message.createdAt)}
                  </span>
                  <span className="h-px flex-1 bg-line" />
                </div>
              )}
              <div className={`group flex gap-2.5 ${showHeader ? 'mt-2' : 'mt-0.5'}`}>
                <div className="w-8 shrink-0">
                  {showHeader ? (
                    <Avatar message={message} />
                  ) : (
                    <span
                      className="block select-none pt-0.5 text-center text-2xs text-transparent group-hover:text-ink-faint"
                      aria-hidden="true"
                    >
                      {clockTime(message.createdAt)}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  {showHeader && (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-semibold text-ink">{senderName(message)}</span>
                      {message.role === 'pm' && (
                        <span className="rounded bg-surface-2 px-1 py-0.5 font-display text-2xs font-bold uppercase tracking-wide text-ink-faint">
                          PM
                        </span>
                      )}
                      <span className="text-2xs text-ink-faint">{clockTime(message.createdAt)}</span>
                    </div>
                  )}
                  <div className="text-sm text-ink">
                    <Markdown className="prose-sm">{message.body}</Markdown>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {isPmThinking && (
            <div className="mt-2 flex gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink text-surface-1">
                <Bot className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="flex items-center gap-2 pt-1 text-sm text-ink-muted">
                <PmThinkingDots />
                <span>PM is thinking…</span>
              </div>
            </div>
          )}

          {lastTurnFailed && !isPmThinking && (
            <div className="mt-2 pl-[2.625rem] text-xs italic text-ink-faint">
              The PM couldn&apos;t reply — post again to retry.
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {showJumpPill && (
          <button
            type="button"
            onClick={() => scrollToBottom('smooth')}
            className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-xs font-medium text-surface-1 shadow-lg transition-colors hover:bg-ink/85"
          >
            <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
            Jump to latest
          </button>
        )}
      </div>

      {readOnly ? (
        <p className="mt-3 rounded-lg border border-line bg-surface-2 px-4 py-3 text-center text-sm text-ink-faint">
          {readOnlyMessage}
        </p>
      ) : (
        <div className="mt-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              className="max-h-[150px] min-h-[44px] flex-1 resize-none rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
              placeholder="Post to the thread…"
              rows={1}
              maxLength={MAX_LEN}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={sendMutation.isPending}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              type="button"
              className="h-[44px] shrink-0 rounded-lg bg-signal px-3 text-white transition-colors hover:bg-signal/90 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={!draft.trim() || sendMutation.isPending}
              onClick={handleSend}
              aria-label="Post"
            >
              {sendMutation.isPending ? <Spinner className="h-4 w-4" /> : <Send className="h-4 w-4" aria-hidden="true" />}
            </button>
          </div>
          {draft.length >= COUNTER_THRESHOLD && (
            <p className="mt-1 text-right text-2xs text-ink-faint">
              {draft.length.toLocaleString()} / {MAX_LEN.toLocaleString()}
            </p>
          )}
          {sendMutation.isError && (
            <Callout
              tone="danger"
              className="mt-2"
              actions={
                <button type="button" className="btn-secondary" onClick={handleSend} disabled={sendMutation.isPending}>
                  Retry
                </button>
              }
            >
              {errorMessage(sendMutation.error, 'Could not post')}
            </Callout>
          )}
        </div>
      )}
    </div>
  );
}
